import type { U2000TableColumn } from "../../components/tables/basic/U2000BasicTableList";

export const U2000GridStateEnumAction = {
  SETCOLUMNS: "SETCOLUMNS",
  SETDATA: "SETDATA",
  SETDATABYID: "SETDATABYID",
  SETPAGINATION: "SETPAGINATION",
  SETTOTALROWS: "SETTOTALROWS",
  SETSELECTEDFIELDS: "SETSELECTEDFIELDS",
  SETSELECTEDSORTEDFIELDS: "SETSELECTEDSORTEDFIELDS",
  SETCURRENTIDX: "SETCURRENTIDX",
  SETPAGE: "SETPAGE",
  SETSIZE: "SETSIZE",
  GRIDERRORS: "GRIDERRORS",
} as const;

export const U2000GridStateEnumPaginationDirection = {
  NEXTPAGE: "NEXTPAGE",
  PREVIOUSPAGE: "PREVIOUSPAGE",
} as const;

export type U2000GridStateEnumPaginationDirection = (typeof U2000GridStateEnumPaginationDirection)[keyof typeof U2000GridStateEnumPaginationDirection];

export interface Pagination {
  page: number;
  size: number;
}

export interface U2000GridState<T = any> {
  columns: U2000TableColumn[];
  data: T[];
  selectedFields: string[];
  selectedSortedFields: { name: string; sortOrder?: string }[];
  pagination: Pagination;
  totalRows: number;
  currentIdx: number;
  errors: string[];
}

export interface U2000GridAction {
  type: string;
  [key: string]: any;
}

function handleSetColumns(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { columns } = action.data;

  return {
    ...gridState,
    columns: columns,
  };
}
function handleSetData(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { rows } = action.data;

  return {
    ...gridState,
    data: rows,
  };
}

function handleSetDataById(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { id, row } = action.data;

  return {
    ...gridState,
    data: gridState.data.map((item) => (item.id === id ? row : item)),
  };
}

function handleSetPagination(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { pagination } = action.data;

  return {
    ...gridState,
    pagination: pagination,
  };
}

function handleSetTotalRows(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { totalRows } = action.data;

  return {
    ...gridState,
    totalRows: totalRows,
  };
}

function handleSetSelectedFields(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { name } = action.data;

  return {
    ...gridState,
    selectedFields: name,
  };
}

function handleSetSelectedSortedFields(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { sortedFields } = action.data;

  return {
    ...gridState,
    selectedSortedFields: sortedFields,
  };
}

function handleSetCurrentIdx(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { idx } = action.data;

  return {
    ...gridState,
    currentIdx: idx,
  };
}

function handleSetPage(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { page } = action.data;

  return {
    ...gridState,
    pagination: {
      ...gridState.pagination,
      page,
    },
  };
}

function handleSetSize(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { size } = action.data;

  return {
    ...gridState,
    pagination: {
      ...gridState.pagination,
      size,
    },
  };
}

function handleGridErrors(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const { errors } = action.data;

  return {
    ...gridState,
    errors: errors,
  };
}

const actionHandlers: Record<string, (gridState: U2000GridState, action: U2000GridAction) => U2000GridState> = {
  [U2000GridStateEnumAction.SETDATA]: handleSetData,
  [U2000GridStateEnumAction.SETDATABYID]: handleSetDataById,
  [U2000GridStateEnumAction.SETCURRENTIDX]: handleSetCurrentIdx,
  [U2000GridStateEnumAction.SETCOLUMNS]: handleSetColumns,
  [U2000GridStateEnumAction.SETSELECTEDFIELDS]: handleSetSelectedFields,
  [U2000GridStateEnumAction.SETSELECTEDSORTEDFIELDS]: handleSetSelectedSortedFields,
  [U2000GridStateEnumAction.SETPAGINATION]: handleSetPagination,
  [U2000GridStateEnumAction.SETTOTALROWS]: handleSetTotalRows,
  [U2000GridStateEnumAction.SETPAGE]: handleSetPage,
  [U2000GridStateEnumAction.SETSIZE]: handleSetSize,
  [U2000GridStateEnumAction.GRIDERRORS]: handleGridErrors,
};

export default function u2000GridState(gridState: U2000GridState, action: U2000GridAction): U2000GridState {
  const handler = actionHandlers[action.type];
  if (!handler) {
    throw new Error(`Unhandled action type: ${action.type}`);
  }
  return handler(gridState, action);
}
